import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import {
  AvailableColumnCalculations,
  AvailableLineData,
  ForecastComparisonResponse,
  ImageCreationResponse,
  ImageResponse,
  LiveReportDetail,
  LiveReportList,
  LiveReportRequest,
  RowFormula,
  SingleOrgForecastComparisonRequest
} from '@futrli-sdk/predict';

import { AvailableDatasource } from '../../../report-builder/available-forecasts';
import { FinData } from '../../../report-builder/types';
import { ReferencedDatasource } from '../../../report-builder/utils-data';
import { V2FinancialDatasetRequest } from '../../../report-builder/utils-queries';
import { makeRequestWithResponse } from '../shared';

/**
 * The payload accepted by report create/update requests.
 *
 * `referenced_datasources` is relaxed from the generated `LiveReportRequest` type so that it can
 * also carry consolidation datasources and omit `forecast_id` for the actuals kinds.
 */
export type LiveReportPayload = Partial<Omit<LiveReportRequest, 'referenced_datasources'>> & {
  referenced_datasources?: ReferencedDatasource[];
};

/**
 * Fetch Available Financial Fields
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Available Financial Fields
 * @throws error
 */
export const fetchAvailableLineData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<AvailableLineData> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_LINE_DATA_CONSOLIDATION
    : API_PATHS.AVAILABLE_LINE_DATA_REPORTING_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AvailableLineData>({ url: path });
};

/**
 * Fetch Row formula data
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Available row formula data
 * @throws error
 */
export const fetchAvailableRowFormulaData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<RowFormula[]> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_ROW_FORMULA_DATA_CONSOLIDATION
    : API_PATHS.AVAILABLE_ROW_FORMULA_DATA_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<RowFormula[]>({ url: path });
};

/**
 * Fetch column calculation data
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 */
export const fetchColumnCalculationsData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<AvailableColumnCalculations> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.COLUMN_CALCULATIONS_CONSOLIDATION : API_PATHS.COLUMN_CALCULATIONS,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
    }
  );

  return makeRequestWithResponse<AvailableColumnCalculations>({ url: path });
};

export const createNewRowFormula = async (
  workspaceId: string,
  organisationId: string,
  data: Partial<RowFormula>,
  isConsolidation?: boolean
): Promise<RowFormula> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_ROW_FORMULA_DATA_CONSOLIDATION
    : API_PATHS.AVAILABLE_ROW_FORMULA_DATA_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<RowFormula>({ url: path, method: REQUEST_METHODS.POST, data });
};

export const updateRowFormula = async (
  workspaceId: string,
  organisationId: string,
  rowFormulaId: string,
  data: Partial<RowFormula>,
  isConsolidation?: boolean
): Promise<RowFormula> => {
  const route = isConsolidation ? API_PATHS.ROW_FORMULA_DATA_BY_ID_CONSOLIDATION : API_PATHS.ROW_FORMULA_DATA_BY_ID_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ROW_FORMULA_ID]: rowFormulaId
  });

  return makeRequestWithResponse<RowFormula>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

/**
 * Delete row formula
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} rowFormulaId - organisation to get plays from
 */
export const deleteRowFormula = async (
  workspaceId: string,
  organisationId: string,
  rowFormulaId: string,
  isConsolidation?: boolean
): Promise<void> => {
  const route = isConsolidation ? API_PATHS.ROW_FORMULA_DATA_BY_ID_CONSOLIDATION : API_PATHS.ROW_FORMULA_DATA_BY_ID_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ROW_FORMULA_ID]: rowFormulaId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};

/**
 * Fetch Reports
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<LiveReportList[]>} - Returns Reports
 * @throws error
 */
export const fetchLiveReports = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<LiveReportList[]> => {
  const path = interpolatePathParams(isConsolidation ? API_PATHS.LIVE_REPORTS_CONSOLIDATION : API_PATHS.LIVE_REPORTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<LiveReportList[]>({ url: path });
};

/**
 * Fetch Live Report by ID
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Reports
 * @throws error
 */
export const fetchLiveReportById = async (
  workspaceId: string,
  organisationId: string,
  id: string,
  isConsolidation?: boolean
): Promise<LiveReportDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.LIVE_REPORT_BY_ID_CONSOLIDATION : API_PATHS.LIVE_REPORT_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.REPORT_ID]: id
    }
  );

  return makeRequestWithResponse<LiveReportDetail>({ url: path });
};

/**
 * Fetch the datasources shared with the current user for a report
 *
 * @param {string} workspaceId - workspace the report belongs to
 * @param {string} organisationId - organisation the report belongs to
 * @param {string} reportId - report to get the available datasources for
 * @param {boolean} isConsolidation - whether the report belongs to a consolidation
 * @returns {Promise<AvailableDatasource[]>} - Returns the available datasources
 * @throws error
 */
export const fetchAvailableDatasources = async (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  isConsolidation?: boolean
): Promise<AvailableDatasource[]> => {
  const route = isConsolidation
    ? API_PATHS.LIVE_REPORT_AVAILABLE_DATASOURCES_CONSOLIDATION
    : API_PATHS.LIVE_REPORT_AVAILABLE_DATASOURCES;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.REPORT_ID]: reportId
  });

  return makeRequestWithResponse<AvailableDatasource[]>({ url: path });
};

/**
 * Create new report
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Reports
 * @throws error
 */
export const createNewLiveReport = async (
  workspaceId: string,
  organisationId: string,
  report: {
    name: string;
    locale?: string;
    data?: unknown;
    status?: 'draft' | 'live';
    is_locked?: boolean;
    orientation?: 'portrait' | 'landscape' | 'dashboard';
    page_count?: number;
    referenced_datasources?: ReferencedDatasource[];
  },
  isConsolidation?: boolean
): Promise<LiveReportDetail> => {
  const path = interpolatePathParams(isConsolidation ? API_PATHS.LIVE_REPORTS_CONSOLIDATION : API_PATHS.LIVE_REPORTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<LiveReportDetail>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: {
      name: report.name || '',
      data: report.data || { pageSetup: { orientation: 'portrait' } },
      status: 'draft',
      is_locked: false,
      locale: report.locale,
      orientation: report.orientation || 'portrait',
      page_count: report.page_count ?? 1,
      // A newly created report has no widgets yet, so it references nothing until the first save.
      referenced_datasources: report.referenced_datasources ?? []
    }
  });
};

/**
 * Duplicate report
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Reports
 * @throws error
 */
export const duplicateLiveReport = async (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  reportName: string,
  isConsolidation?: boolean
): Promise<LiveReportDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.LIVE_REPORTS_DUPLICATE_BY_ID_CONSOLIDATION : API_PATHS.LIVE_REPORTS_DUPLICATE_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.REPORT_ID]: reportId
    }
  );

  return makeRequestWithResponse<LiveReportDetail>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: { name: reportName }
  });
};

/**
 * Delete report
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Reports
 * @throws error
 */
export const deleteLiveReport = async (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  isConsolidation?: boolean
): Promise<void> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.LIVE_REPORT_BY_ID_CONSOLIDATION : API_PATHS.LIVE_REPORT_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.REPORT_ID]: reportId
    }
  );

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};

/**
 * Patch report
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Reports
 * @throws error
 */
export const patchLiveReport = async (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  data: LiveReportPayload,
  isConsolidation?: boolean
): Promise<LiveReportDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.LIVE_REPORT_BY_ID_CONSOLIDATION : API_PATHS.LIVE_REPORT_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.REPORT_ID]: reportId
    }
  );

  return makeRequestWithResponse<LiveReportDetail>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

export interface BulkReportDataQueries {
  datasets?: Record<string, V2FinancialDatasetRequest>;
  comparisons?: Record<string, SingleOrgForecastComparisonRequest>;
}

export interface BulkReportDataResponse {
  datasets: Record<string, FinData>;
  comparisons: Record<string, ForecastComparisonResponse>;
}

const getFinancialDataRoute = (isConsolidation?: boolean) =>
  isConsolidation ? API_PATHS.LIVE_REPORTS_FINANCIAL_DATA_CONSOLIDATION : API_PATHS.LIVE_REPORTS_FINANCIAL_DATA_V2;

/**
 * Get bulk report data (datasets and/or comparisons) in a single financial-data request.
 *
 * @param {string} workspaceId - workspace the organisation belongs to
 * @param {string} organisationId - organisation (or consolidation) to fetch data for
 * @param {BulkReportDataQueries} queries - V2 dataset and/or comparison queries
 * @param {boolean} [isConsolidation] - whether this is a consolidated organisation report
 * @param {string} [reportId] - id of the report the data is being fetched for. Omit when the
 * request is not made in the context of a specific report (actuals-only data)
 * @param {boolean} [isWorkspaceTemplate] - whether `reportId` is the id of a workspace template, which is sent
 * as `template_id` instead of `report_id`
 * @returns {Promise<BulkReportDataResponse>} - Returns datasets and comparisons
 * @throws error
 */
export const getReportBulkData = async (
  workspaceId: string,
  organisationId: string,
  queries: BulkReportDataQueries,
  isConsolidation?: boolean,
  reportId?: string,
  isWorkspaceTemplate?: boolean
): Promise<BulkReportDataResponse> => {
  const path = interpolatePathParams(getFinancialDataRoute(isConsolidation), {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  const data: BulkReportDataQueries = {};

  if (queries.datasets && Object.keys(queries.datasets).length) {
    data.datasets = queries.datasets;
  }

  if (queries.comparisons && Object.keys(queries.comparisons).length) {
    data.comparisons = queries.comparisons;
  }

  const res = await makeRequestWithResponse<Partial<BulkReportDataResponse>>({
    url: path,
    method: REQUEST_METHODS.POST,
    params: reportId ? { [isWorkspaceTemplate ? 'template_id' : 'report_id']: reportId } : undefined,
    data
  });

  return {
    datasets: res.datasets || {},
    comparisons: res.comparisons || {}
  };
};

/**
 * Get bulk dataset data for reports.
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get data for
 * @param {Record<string, V2FinancialDatasetRequest>} queries - V2 dataset queries keyed by widget
 * @param {boolean} [isConsolidation] - whether this is a consolidated organisation report
 * @param {string} [reportId] - id of the report the data is being fetched for. Omit when the
 * request is not made in the context of a specific report (actuals-only data)
 * @param {boolean} [isWorkspaceTemplate] - whether `reportId` is the id of a workspace template, which is sent
 * as `template_id` instead of `report_id`
 * @returns {Promise<Record<string, FinData>>} - Returns Live Data
 * @throws error
 */
export const getReportBulkLiveData = async (
  workspaceId: string,
  organisationId: string,
  queries: Record<string, V2FinancialDatasetRequest>,
  isConsolidation?: boolean,
  reportId?: string,
  isWorkspaceTemplate?: boolean
): Promise<Record<string, FinData>> => {
  const { datasets } = await getReportBulkData(
    workspaceId,
    organisationId,
    { datasets: queries },
    isConsolidation,
    reportId,
    isWorkspaceTemplate
  );
  return datasets;
};

/**
 * Get S3 Image upload link, id and keys
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} name - name of the image file
 * @param {string} level - access level of the image file
 * @param isConsolidation - wether or not this is for a consolidated organisation report
 * @returns {Promise<ImageCreationResponse>} - Returns
 * @throws error
 */
export const getImageUploadLink = async (
  workspaceId: string,
  organisationId: string,
  name: string,
  level: 'workspace' | 'organisation',
  isConsolidation?: boolean
): Promise<ImageCreationResponse> => {
  const url = interpolatePathParams(isConsolidation ? API_PATHS.IMAGES_CONSOLIDATION : API_PATHS.IMAGES, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ImageCreationResponse>({ url, method: REQUEST_METHODS.POST, data: { name, level } });
};

/**
 * Get list of uploaded images
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param isConsolidation - wether or not this is for a consolidated organisation report
 * @returns {Promise<ImageResponse[]>} - Returns
 * @throws error
 */
export const fetchReportImages = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<ImageResponse[]> => {
  const url = interpolatePathParams(isConsolidation ? API_PATHS.IMAGES_CONSOLIDATION : API_PATHS.IMAGES, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ImageResponse[]>({ url, method: REQUEST_METHODS.GET });
};

/**
 * Delete uploaded image
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} imgId - id of image to delete
 * @param isConsolidation - wether or not this is for a consolidated organisation report
 * @returns {Promise<void>} - Returns
 * @throws error
 */
export const deleteReportImage = async (
  workspaceId: string,
  organisationId: string,
  imgId: string,
  isConsolidation?: boolean
): Promise<void> => {
  const url = interpolatePathParams(isConsolidation ? API_PATHS.IMAGE_CONSOLIDATION : API_PATHS.IMAGE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.IMAGE_ID]: imgId
  });

  return makeRequestWithResponse<void>({ url, method: REQUEST_METHODS.DELETE });
};
